import { AlertCircle, ArrowRight, Check, Clock, Link2Off, Lock, Minus, UserRound, UserX } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { type AppDispatch } from "@/app/store";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaPasswordField } from "@/shared/components/forms/ColocaPasswordField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { AuthShell } from "../components/AuthShell";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter";
import { useAuth } from "../hooks/useAuth";
import { invitationService } from "../services/invitationService";
import { thunkEstablishInvitationSession } from "../store/authThunks";
import type { VerifyInvitationResponse } from "../types/invitation.types";
import {
  createAcceptInvitationSchema,
  type AcceptInvitationFormValues,
  type AcceptInvitationSchemaTranslator,
} from "../utils/acceptInvitationSchema";
import { scorePassword } from "../utils/passwordStrength";

/** "Acme Corp" → "AC" for the organization monogram (first letter of the first two words). */
function orgMonogram(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? "")
    .join("")
    .toUpperCase();
}

type PageStatus = "verifying" | "form" | "invalid" | "load_error" | "conflict" | "success";

/** Delay before entering the app after a successful accept — long enough to read the welcome, no dead end. */
const SUCCESS_REDIRECT_MS = 1400;

/**
 * Public accept-invitation view (`/auth/invitations/accept?token&email`) — the landing of the magic link in
 * the invitation email. Flow: verify the link (`GET /auth/invitations/verify`) → show the form (name +
 * password + confirm) → `POST /auth/invitations/accept` → the 201 already carries the session (cookies +
 * tokens in body outside production), so the user goes straight into the dashboard. Declared OUTSIDE
 * PublicRoute: someone with an active session may follow an invite meant for a different account.
 */
export const InvitationAcceptPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslations("login");
  const { user, isAuthenticated } = useAuth();

  const token = searchParams.get("token") ?? "";
  const email = searchParams.get("email") ?? "";

  // A link without its params is invalid from the start — resolved at initialization, not via effect.
  const [status, setStatus] = useState<PageStatus>(() => (!token || !email ? "invalid" : "verifying"));
  const [invitation, setInvitation] = useState<VerifyInvitationResponse | null>(null);
  // Bumped by the retry button after a network/server failure to re-run the verify effect.
  const [verifyAttempt, setVerifyAttempt] = useState(0);

  useEffect(() => {
    if (!token || !email) return;
    let cancelled = false;
    invitationService
      .verify(email, token)
      .then((data) => {
        if (cancelled) return;
        setInvitation(data);
        setStatus("form");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // The backend answers a single generic 400 for expired/used/revoked links. Anything else (network,
        // 5xx, 429) is NOT evidence the link is bad — offer a retry instead of a lying "invalid" screen.
        const httpStatus = HttpApiService.isAxiosError(error) ? error.response?.status : undefined;
        setStatus(httpStatus === 400 ? "invalid" : "load_error");
      });
    return () => {
      cancelled = true;
    };
  }, [email, token, verifyAttempt]);

  // Transient success screen, then straight into the dashboard (the session is already established).
  useEffect(() => {
    if (status !== "success") return;
    const timer = setTimeout(() => navigate("/app/wallets", { replace: true }), SUCCESS_REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [status, navigate]);

  const sessionMismatch =
    status === "form" && isAuthenticated && !!user && !!invitation && user.email !== invitation.email;

  return (
    <AuthShell panelSide="right">
      {/* min-h-0 + overflow: on short viewports the form scrolls inside the centered column instead of
          clipping (AuthShell is a fixed h-screen flex without page scroll). */}
      <div className="flex min-h-0 w-full max-w-md flex-col overflow-y-auto">
        {status === "verifying" && (
          <div aria-busy="true" aria-label={t("inv_verifying_aria")} className="flex flex-col gap-4">
            <ColocaSkeleton className="h-[52px] w-[52px] rounded-[13px]" />
            <ColocaSkeleton className="h-5 w-72" />
            <ColocaSkeleton className="h-3.5 w-52" />
            <div className="mt-4 flex flex-col gap-4">
              <ColocaSkeleton className="h-11 w-full rounded-[10px]" />
              <ColocaSkeleton className="h-11 w-full rounded-[10px]" />
              <ColocaSkeleton className="h-11 w-full rounded-[10px]" />
              <ColocaSkeleton className="h-11 w-full rounded-[10px] opacity-60" />
            </div>
          </div>
        )}

        {status === "form" && invitation && (
          <InvitationAcceptForm
            invitation={invitation}
            email={email}
            token={token}
            sessionMismatch={sessionMismatch}
            currentEmail={user?.email}
            onConflict={() => setStatus("conflict")}
            onInvalidLink={() => setStatus("invalid")}
            onSuccess={() => setStatus("success")}
          />
        )}

        {status === "success" && invitation && (
          <div role="status" className="flex flex-col gap-4 animate-fade-up">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-success-container text-success-container-foreground">
              <Check className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {t("inv_success_title").replace("{org}", invitation.organization_name)}
            </h1>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <span
                aria-hidden
                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"
              />
              {t("inv_success_entering")}
            </p>
          </div>
        )}

        {status === "invalid" && (
          <div className="flex flex-col gap-4 animate-fade-up">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-muted text-muted-foreground">
              <Link2Off className="h-5.5 w-5.5" />
            </div>
            <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground">
              {t("inv_invalid_title")}
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground">{t("inv_invalid_body")}</p>
            <Link
              to="/auth/login"
              className="mt-1 self-start text-sm font-medium text-foreground underline underline-offset-4 hover:text-primary"
            >
              {t("inv_go_login")}
            </Link>
          </div>
        )}

        {status === "load_error" && (
          <div className="flex flex-col gap-4 animate-fade-up">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-muted text-muted-foreground">
              <AlertCircle className="h-5.5 w-5.5" />
            </div>
            <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground">
              {t("inv_error_title")}
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground">{t("inv_error_body")}</p>
            <ColocaButton
              variant="primary"
              size="sm"
              className="self-start"
              onClick={() => {
                // Back to the skeleton while the re-verify is in flight (event handler, not effect).
                setStatus("verifying");
                setVerifyAttempt((n) => n + 1);
              }}
            >
              {t("inv_retry")}
            </ColocaButton>
          </div>
        )}

        {status === "conflict" && (
          <div className="flex flex-col gap-4 animate-fade-up">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-warning-container text-warning-container-foreground">
              <UserX className="h-5.5 w-5.5" />
            </div>
            <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground">
              {t("inv_conflict_title")}
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("inv_conflict_body").replace("{email}", email)}
            </p>
            <ColocaButton
              variant="primary"
              size="lg"
              className="mt-1 self-start"
              onClick={() => navigate("/auth/login")}
            >
              {t("inv_conflict_login")}
            </ColocaButton>
          </div>
        )}
      </div>
    </AuthShell>
  );
};

interface InvitationAcceptFormProps {
  invitation: VerifyInvitationResponse;
  email: string;
  token: string;
  sessionMismatch: boolean;
  currentEmail?: string;
  onConflict: () => void;
  onInvalidLink: () => void;
  onSuccess: () => void;
}

/**
 * The accept form. Mounted only after a successful verify so the schema's `nameRequired` is fixed at mount
 * (the invitation either carried a name or it didn't). Password + confirm follow the house pattern
 * (`ResetPasswordPage` / `createResetPasswordSchema`): live requirement checklist, strength meter, match
 * indicator, and the mismatch error attached to the confirm field.
 */
function InvitationAcceptForm({
  invitation,
  email,
  token,
  sessionMismatch,
  currentEmail,
  onConflict,
  onInvalidLink,
  onSuccess,
}: InvitationAcceptFormProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { t, activeLanguage } = useTranslations("login");
  const { t: tg } = useTranslations("global");

  const [error, setError] = useState<string | null>(null);
  const [isThrottled, setIsThrottled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  // The backend falls back to the invitation's own name, so the field is only mandatory when that is absent.
  const nameRequired = !invitation.full_name?.trim();

  const schemaTranslator: AcceptInvitationSchemaTranslator = useMemo(
    () =>
      (key): string => {
        switch (key) {
          case "name_required":
            return t("inv_name_required");
          case "name_max":
            return t("inv_name_max");
          case "required":
            return tg("input_error_required");
          case "min":
            return t("fp_new_req_length");
          case "max":
            return t("inv_password_max");
          case "number":
            return t("fp_new_req_number");
          case "symbol":
            return t("fp_new_req_symbol");
          case "match":
            return t("fp_new_match_error");
        }
      },
    [t, tg],
  );

  const schema = useMemo(
    () => createAcceptInvitationSchema(schemaTranslator, nameRequired),
    [schemaTranslator, nameRequired],
  );
  const form = useZodForm(
    schema,
    { fullName: invitation.full_name ?? "", password: "", confirmPassword: "" },
    "onChange",
  );
  const password = form.watch("password");
  const confirmPassword = form.watch("confirmPassword");
  const fullName = form.watch("fullName");

  const { rules } = scorePassword(password ?? "");
  // Submit gate mirrors the BACKEND rules exactly (8+, number, symbol) — `longLength` is advisory only.
  const passwordOk = rules.minLength && rules.hasNumber && rules.hasSymbol;
  const passwordsMatch = !!password && password === confirmPassword;
  const nameOk = !nameRequired || !!fullName?.trim();
  const canSubmit = passwordOk && passwordsMatch && nameOk && !isLoading;

  const requirements: { label: string; ok: boolean }[] = [
    { label: t("fp_new_req_length"), ok: rules.minLength },
    { label: t("fp_new_req_number"), ok: rules.hasNumber },
    { label: t("fp_new_req_symbol"), ok: rules.hasSymbol },
  ];

  const strengthLabels = [
    t("fp_new_strength_empty"),
    t("fp_new_strength_weak"),
    t("fp_new_strength_fair"),
    t("fp_new_strength_good"),
    t("fp_new_strength_strong"),
  ] as const;

  const expiryDate = useMemo(() => {
    const date = new Date(invitation.expires_at);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(activeLanguage, { dateStyle: "long" }).format(date);
  }, [invitation.expires_at, activeLanguage]);

  const handleSubmit = async (values: AcceptInvitationFormValues) => {
    setError(null);
    setIsThrottled(false);
    setIsLoading(true);
    try {
      const result = await invitationService.accept({
        email,
        token,
        password: values.password,
        // Empty (only possible when the invitation already carries a name) → omit; backend falls back.
        full_name: values.fullName.trim() || undefined,
      });
      dispatch(
        thunkEstablishInvitationSession({
          accessToken: result.access_token,
          refreshToken: result.refresh_token,
          user: result.user,
        }),
      );
      onSuccess();
    } catch (err: unknown) {
      const httpStatus = HttpApiService.isAxiosError(err) ? err.response?.status : undefined;
      if (httpStatus === 409) {
        onConflict();
      } else if (httpStatus === 429) {
        setIsThrottled(true);
      } else if (httpStatus === 400) {
        // Client validation already mirrors the DTO, so a 400 here means the token died between verify and
        // submit (expired, revoked, or consumed by a concurrent accept).
        onInvalidLink();
      } else {
        setError(HttpApiService.getErrorMessage(err) || t("fp_error_network"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 animate-fade-up">
      {sessionMismatch && !noticeDismissed && (
        <div
          role="status"
          className="mb-1 flex items-start gap-2.5 rounded-[10px] border border-warning/40 bg-warning-container px-3.5 py-3 text-[13px] leading-relaxed text-warning-container-foreground"
        >
          <UserRound className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span>
              {t("inv_session_notice")
                .replace("{current}", currentEmail ?? "")
                .replace("{invited}", invitation.email)}
            </span>
            <button
              type="button"
              onClick={() => setNoticeDismissed(true)}
              className="self-start text-[13px] font-semibold underline underline-offset-2"
            >
              {t("inv_session_continue")}
            </button>
          </div>
        </div>
      )}

      {/* Organization context hero */}
      <div className="mb-3 flex flex-col gap-2.5">
        <div
          aria-hidden
          className="grid h-[52px] w-[52px] place-items-center rounded-[13px] bg-primary/10 text-[19px] font-semibold tracking-wide text-primary"
        >
          {orgMonogram(invitation.organization_name)}
        </div>
        <span className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.06em] text-muted-foreground">
          {t("inv_eyebrow")}
        </span>
        <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground">
          {t("inv_title").replace("{org}", invitation.organization_name)}
        </h1>
      </div>

      {isThrottled && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-[10px] bg-warning-container px-3.5 py-2.5 text-[13px] text-warning-container-foreground animate-fade-up"
        >
          <Clock className="h-3.5 w-3.5 shrink-0" />
          {t("inv_throttled")}
        </div>
      )}

      <ColocaForm
        key={activeLanguage}
        form={form}
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 space-y-0"
      >
        {/* Locked email — bound to the token, never editable */}
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-foreground">{t("inv_email_label")}</span>
          <div className="flex h-11 items-center gap-2 rounded-[10px] border border-dashed border-border bg-muted px-3.5">
            <Lock aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate font-mono text-[13px] text-foreground">{invitation.email}</span>
          </div>
        </div>

        <ColocaInputField
          name="fullName"
          label={t("inv_name_label")}
          placeholder={t("inv_name_placeholder")}
          autoComplete="name"
        />

        <ColocaPasswordField
          name="password"
          label={t("inv_password_label")}
          placeholder={t("inv_password_placeholder")}
          showLabel={tg("input_show_password_label")}
          hideLabel={tg("input_hide_password_label")}
        />

        <div className="space-y-1">
          <PasswordStrengthMeter
            value={password ?? ""}
            labels={strengthLabels}
            ariaLabel={t("fp_new_strength")}
          />
          <ul className="space-y-1 pt-2" aria-live="polite">
            {requirements.map((req) => (
              <li
                key={req.label}
                className={cn(
                  "flex items-center gap-2 text-xs transition-colors",
                  req.ok ? "text-primary" : "text-muted-foreground",
                )}
              >
                <span
                  className={cn(
                    "grid h-4 w-4 place-items-center rounded-full transition-colors",
                    req.ok ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
                  )}
                >
                  {req.ok ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                </span>
                {req.label}
              </li>
            ))}
          </ul>
        </div>

        <ColocaPasswordField
          name="confirmPassword"
          label={t("inv_confirm_label")}
          placeholder={t("inv_confirm_placeholder")}
          showLabel={tg("input_show_password_label")}
          hideLabel={tg("input_hide_password_label")}
        />
        {confirmPassword && passwordsMatch && (
          <p className="inline-flex items-center gap-1 text-xs font-medium text-primary">
            <Check className="h-3 w-3" />
            {t("fp_new_match_ok")}
          </p>
        )}

        {error && (
          <div className="animate-shake">
            <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />
          </div>
        )}

        <ColocaButton
          type="submit"
          variant="primary"
          size="lg"
          loading={isLoading}
          disabled={!canSubmit}
          className="h-12 w-full text-base font-semibold"
          iconTrailing={ArrowRight}
        >
          {t("inv_submit").replace("{org}", invitation.organization_name)}
        </ColocaButton>

        {expiryDate && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t("inv_expiry_note").replace("{date}", expiryDate)}
          </p>
        )}
      </ColocaForm>

      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t("inv_footer_ignore")}</p>
    </div>
  );
}

export default InvitationAcceptPage;
